import React, { useState } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { abacUserEntity } from '../../config/entities/abacUsers';
import { abacUserService } from '../../services/abac';
import { Modal } from '../../components/ui';
import { useLanguage } from '../../state/language/LanguageProvider';
import AttributeAssignmentModal from '../../components/abac/AttributeAssignmentModal';
import { toast } from 'react-hot-toast';

interface User {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  status?: string;
  user_type?: 'CURRENT_TENANT' | 'PARTNER_TENANT';
  tenant_name?: string;
  custom_attributes?: Array<{
    value_id: string;
    attribute_name: string;
    value: any;
    data_type?: string;
  }>;
}

const ViewUsersPage: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAttributeAssignment, setShowAttributeAssignment] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { t } = useLanguage();

  const handleAssignAttributes = (user: User) => {
    setSelectedUser(user);
    setShowAttributeAssignment(true);
  };

  const handleAttributeAssigned = () => {
    // Refresh the table data
    setRefreshKey(prev => prev + 1);
  };

  const handleDeleteAttributeValue = async (valueId: string) => {
    try {
      await abacUserService.deleteAttributeValue(valueId);
      toast.success(t('abac.attributeValueDeleted'));
      // Refresh the table data
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete attribute value:', error);
      toast.error(t('abac.failedToDeleteAttributeValue'));
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.assignUserAttributesAndValues')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.assignUserAttributesAndValuesDescription')}
        </p>
      </div>

      <FlexibleCRUDTable
        key={refreshKey}
        entity={{
          ...abacUserEntity,
          fields: abacUserEntity.fields.map(field => {
            if (field.key === 'custom_attributes') {
              return {
                ...field,
                render: (value: any[], _user: any) => {
                  if (!value || value.length === 0) {
                    return 'No attributes assigned';
                  }
                  
                  const formatValue = (val: any) => {
                    if (val === null || val === undefined) return 'N/A';
                    
                    // Check if it's a date string (ISO format)
                    if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
                      try {
                        return new Date(val).toLocaleDateString();
                      } catch {
                        return val;
                      }
                    }
                    
                    // Check if it's a boolean
                    if (typeof val === 'boolean') {
                      return val ? 'Yes' : 'No';
                    }
                    
                    return String(val);
                  };
                  
                  return (
                    <div className="space-y-1 max-w-xs">
                      {value.map((attr: any, index: number) => (
                        <div key={index} className="flex items-start justify-between text-xs">
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{attr.attribute_name}</div>
                            <div className="truncate text-gray-600">
                              {Array.isArray(attr.value) 
                                ? attr.value.map((v: any) => formatValue(v)).join(', ')
                                : formatValue(attr.value)
                              }
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttributeValue(attr.value_id);
                            }}
                            className="ml-1 text-red-600 hover:text-red-800 text-xs font-bold flex-shrink-0"
                            title="Delete this attribute value"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                }
              };
            }
            return field;
          })
        }}
        service={abacUserService}
        customActions={[
          {
            label: t('abac.assignAttribute'),
            icon: () => <span>➕</span>, // Using emoji as placeholder icon
            onClick: handleAssignAttributes,
            variant: 'primary'
          }
        ]}
        searchPlaceholder={t('abac.searchUsers')}
      />

      {/* User Details Modal */}
      <Modal
        isOpen={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        title={`User Details: ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('abac.basicInformation')}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('common.name')}:</span>
                  <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('common.email')}:</span>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('common.status')}:</span>
                  <p className="font-medium">{selectedUser.status}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('abac.userID')}:</span>
                  <p className="font-medium">{selectedUser.id}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Attribute Assignment Modal */}
      {selectedUser && (
        <AttributeAssignmentModal
          isOpen={showAttributeAssignment}
          onClose={() => setShowAttributeAssignment(false)}
          entityType="USER"
          entity={selectedUser}
          onAttributeAssigned={handleAttributeAssigned}
        />
      )}
    </div>
  );
};

export default ViewUsersPage;